import type { FC } from 'react';
import { getInitialsFromName } from '@helpers/getInitialsFromName';
import { stringToHslColor } from '@helpers/stringToHslColor';
import type { AuditEntry } from '@root/@types/audit-log';
import type { ResourceBlock } from '@root/@types/base';
import type { Team } from '@root/@types/types';
import dayjs from 'dayjs';

import { EntryCta } from './summaries/ctas';
import { ChangeSummary } from './ChangeSummary';

interface ActivityEntryProps {
  entry: AuditEntry;
  blocks: ResourceBlock[];
  sections: { id: string; label: string }[];
  teams: Team[];
  onJumpToTarget?: (blockId: string) => void;
}

const userDotColor = (name: string) => stringToHslColor(getInitialsFromName(name));

export const ActivityEntry: FC<ActivityEntryProps> = ({ entry, blocks, sections, teams, onJumpToTarget }) => {
  const userName = entry.user?.name ?? 'System';
  const time = dayjs(entry.created_at).format('HH:mm');
  const target = entry.target;

  const jump =
    onJumpToTarget && target && blocks.some((b) => b.id === target.id) ? () => onJumpToTarget(target.id) : undefined;

  return (
    <div className="relative flex items-start gap-3 px-7.5 py-3 hover:bg-blue-50">
      {jump && (
        <button
          type="button"
          aria-label={`Go to ${target?.name ?? 'block'}`}
          className="absolute inset-0 cursor-pointer"
          onClick={jump}
        />
      )}
      <span className="flex h-[18px] shrink-0 items-center">
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: userDotColor(userName) }} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-info-700 text-sm leading-5">
          <ChangeSummary blocks={blocks} sections={sections} teams={teams} item={entry} />
        </div>
        <p className="text-info-400 mt-1 text-xs font-medium">
          by <span>{userName}</span> at {time}
        </p>
        <EntryCta entry={entry} />
      </div>
    </div>
  );
};
